import { createContext, ReactNode, useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { analyticsService } from "../services/analyticsService";
// Context for analytics tracking
// Provides automatic page view tracking via React Router
interface AnalyticsContextType {
  trackPageView: (path: string) => Promise<void>;
  disable: () => void;
  enable: () => void;
}
const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
  undefined,
);
interface AnalyticsProviderProps {
  children: ReactNode;
}
// Provider component that wraps the app and tracks page views
export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const location = useLocation();
  const lastTrackedPath = useRef<string | null>(null);

  // Track page view on route change
  useEffect(() => {
    // Only track if path actually changed
    if (lastTrackedPath.current !== location.pathname) {
      lastTrackedPath.current = location.pathname;
      const trackView = async () => {
        await analyticsService.trackPageView(location.pathname);
      };
      trackView();
    }
  }, [location.pathname]);
  // Track page leave on unmount or before unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      analyticsService.trackPageLeave();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      analyticsService.trackPageLeave();
    };
  }, []);
  const contextValue: AnalyticsContextType = {
    trackPageView: analyticsService.trackPageView.bind(analyticsService),
    disable: analyticsService.disable.bind(analyticsService),
    enable: analyticsService.enable.bind(analyticsService),
  };
  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
}
// Hook to access analytics context
export function useAnalytics(): AnalyticsContextType {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within AnalyticsProvider");
  }
  return context;
}
